<template>
  <div>
    <v-navigation-drawer
      v-model="abierto"
      right
      fixed
      temporary
      :width="$vuetify.breakpoint.xsOnly ? '100%' : 480"
    >
      <div class="d-flex flex-column fill-height">
        <v-toolbar flat dense dark color="blue darken-2" class="flex-grow-0">
          <v-icon left>mdi-robot-outline</v-icon>
          <v-toolbar-title class="subtitle-1">Consultor IA</v-toolbar-title>
          <v-chip x-small class="ml-2" color="white" text-color="blue darken-2">{{ marca }}</v-chip>
          <v-chip
            v-if="versionEfectiva"
            x-small
            class="ml-1"
            :color="esEstructurado ? 'orange darken-2' : 'blue-grey'"
            dark
          >
            {{ etiquetaVersion }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon small title="Nueva conversación" :disabled="enviando || !mensajes.length" @click="reiniciar">
            <v-icon small>mdi-broom</v-icon>
          </v-btn>
          <v-btn icon small title="Cerrar" @click="abierto = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <div ref="lista" class="flex-grow-1 overflow-y-auto pa-3 chat-lista">
          <div v-if="!mensajes.length" class="body-2 grey--text text--darken-1">
            <p>
              Pregunta sobre inversión, prospectos, registros completos, inscritos, sucursales, anuncios o vendedoras.
              Usa los mismos datos del panel; si no indicas periodo, toma el periodo y los filtros que tienes en pantalla.
              Cada recomendación se valida contra los datos antes de mostrarse.
            </p>
            <v-chip
              v-for="s in sugerencias"
              :key="s"
              small
              outlined
              color="blue darken-2"
              class="mr-1 mb-2 chip-sugerencia"
              @click="enviar(s)"
            >
              {{ s }}
            </v-chip>
          </div>

          <div
            v-for="(m, i) in mensajes"
            :key="i"
            class="d-flex mb-3"
            :class="m.rol === 'usuario' ? 'justify-end' : 'justify-start'"
          >
            <div class="burbuja" :class="claseBurbuja(m)">
              <div v-if="m.rol === 'usuario'" class="body-2 texto-usuario">{{ m.texto }}</div>
              <div v-else class="body-2 markdown" v-html="renderizar(m.texto)"></div>
              <div v-if="m.herramientas && m.herramientas.length" class="mt-2">
                <v-chip
                  v-for="(h, j) in m.herramientas"
                  :key="j"
                  x-small
                  class="mr-1 mb-1"
                  :color="h.ok ? 'blue-grey lighten-4' : 'red lighten-4'"
                  :title="JSON.stringify(h.args)"
                >
                  <v-icon x-small left>mdi-database-search</v-icon>
                  {{ etiquetaHerramienta(h) }}
                </v-chip>
              </div>
              <div v-if="m.validacion" class="mt-1">
                <v-chip x-small class="mr-1 mb-1" color="green lighten-4" v-if="m.validacion.validadas">
                  {{ m.validacion.validadas }} validada(s)
                </v-chip>
                <v-chip x-small class="mr-1 mb-1" color="amber lighten-4" v-if="m.validacion.con_observaciones">
                  {{ m.validacion.con_observaciones }} con observaciones
                </v-chip>
                <v-chip x-small class="mr-1 mb-1" color="red lighten-4" v-if="m.validacion.rechazadas">
                  {{ m.validacion.rechazadas }} descartada(s)
                </v-chip>
              </div>
              <div v-if="m.idConsulta" class="caption grey--text mt-1">
                Consulta <span class="font-weight-medium">{{ m.idConsulta }}</span>
                <v-btn x-small icon title="Copiar id de consulta" @click="copiar(m.idConsulta)">
                  <v-icon x-small>mdi-content-copy</v-icon>
                </v-btn>
              </div>
              <div v-if="m.meta" class="caption grey--text">{{ m.meta }}</div>
            </div>
          </div>

          <div v-if="enviando" class="d-flex justify-start mb-3">
            <div class="burbuja burbuja-consultor">
              <div class="body-2 grey--text text--darken-1 mb-1">Consultando datos y analizando…</div>
              <v-progress-linear indeterminate color="blue darken-2" height="3"></v-progress-linear>
            </div>
          </div>
        </div>

        <div class="pa-2 flex-grow-0 chat-entrada">
          <v-textarea
            v-model="pregunta"
            placeholder="Escribe tu pregunta…"
            rows="1"
            auto-grow
            filled
            dense
            hide-details
            :disabled="enviando"
            append-icon="mdi-send"
            @click:append="enviar()"
            @keydown.enter.exact.prevent="enviar()"
          ></v-textarea>
          <div class="caption grey--text mt-1">
            Enter para enviar, Shift+Enter para salto de línea. Verifica las cifras antes de mover presupuesto.
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
  import MarkdownIt from 'markdown-it'

  const md = new MarkdownIt({ html: false, linkify: false, breaks: true })

  const ETIQUETAS = {
    obtener_resumen: 'Resumen',
    obtener_sucursales: 'Sucursales',
    obtener_anuncios: 'Anuncios',
    obtener_vendedoras: 'Vendedoras',
    obtener_presupuesto_ciclo: 'Presupuesto del ciclo',
    obtener_evidencia: 'Evidencia',
    simular_redistribucion: 'Simulación',
  }

  export default {
    props: {
      marca: { type: String, required: true },
      // Periodo y filtros visibles en el panel: { desde, hasta, filtros: { sucursal, producto, ciudad, estratega, anuncio } }
      contexto: { type: Object, default: null },
    },

    data: () => ({
      abierto: false,
      pregunta: '',
      mensajes: [],
      enviando: false,
      versionEfectiva: null,
      sugerencias: [
        '¿Cómo vamos esta semana contra la anterior?',
        '¿Qué sucursal está más cara por nuevo completo?',
        '¿Qué anuncios están perdiendo efectividad?',
        '¿Qué vendedoras convierten mejor este mes?',
        '¿Cómo va el gasto contra el presupuesto del ciclo?',
      ],
    }),

    computed: {
      esEstructurado () {
        return /v[56]|consultor-v[56]/i.test(String(this.versionEfectiva || ''))
      },
      etiquetaVersion () {
        const v = String(this.versionEfectiva || '')
        if (/v6|consultor-v6/i.test(v)) return 'V6'
        if (/v5|consultor-v5/i.test(v)) return 'V5'
        return 'V4'
      },
    },
    methods: {
      abrir () {
        this.abierto = true
        this.bajar()
      },

      renderizar (texto) {
        return md.render(String(texto || ''))
      },

      etiquetaHerramienta (h) {
        const base = ETIQUETAS[h.nombre] || h.nombre
        const a = h.args || {}
        return a.desde && a.hasta ? `${base} ${a.desde} a ${a.hasta}` : base
      },

      claseBurbuja (m) {
        if (m.rol === 'usuario') return 'burbuja-usuario'
        return m.error ? 'burbuja-error' : 'burbuja-consultor'
      },

      copiar (texto) {
        if (navigator.clipboard) navigator.clipboard.writeText(texto)
      },

      reiniciar () {
        this.mensajes = []
        this.pregunta = ''
      },

      bajar () {
        this.$nextTick(() => {
          const el = this.$refs.lista
          if (el) el.scrollTop = el.scrollHeight
        })
      },

      enviar (texto) {
        const pregunta = String(texto || this.pregunta || '').trim()
        if (!pregunta || this.enviando) return
        this.mensajes.push({ rol: 'usuario', texto: pregunta })
        this.pregunta = ''
        this.enviando = true
        this.bajar()
        const historial = this.mensajes
          .filter((m) => !m.error)
          .map((m) => ({ rol: m.rol, texto: m.texto }))
        // version omitida: el backend activa V5 solo si el JWT está en CONSULTOR_IA_V5_USUARIOS
        return this.$http.post('api/ci/consultor/chat', {
          marca: this.marca,
          mensajes: historial,
          contexto: this.contexto,
        })
          .then((response) => {
            const r = response.data || {}
            this.versionEfectiva = r.version_efectiva || r.version_prompt || null
            const segundos = r.duracion_ms ? (r.duracion_ms / 1000).toFixed(1) : null
            const resumen = r.validacion && r.validacion.resumen
            this.mensajes.push({
              rol: 'consultor',
              texto: r.respuesta || 'Sin respuesta.',
              herramientas: r.herramientas || [],
              idConsulta: r.id_consulta || null,
              validacion: resumen && (resumen.validadas || resumen.con_observaciones || resumen.rechazadas) ? resumen : null,
              meta: [
                r.version_efectiva || r.version_prompt,
                r.modelo,
                segundos ? `${segundos} s` : null,
                (r.propuestas_ci_meta && r.propuestas_ci_meta.propuestas)
                  ? `${(r.propuestas_ci_meta.propuestas || []).filter((p) => p.ok && !p.omitida && !p.bloqueada).length} prop.`
                  : null,
              ].filter(Boolean).join(' · '),
            })
          })
          .catch((error) => {
            const data = (error.response && (error.response.data || error.response.body)) || {}
            const msg = data.message || data.mensaje || 'No se pudo obtener la respuesta del consultor.'
            this.mensajes.push({ rol: 'consultor', texto: msg, error: true, idConsulta: data.id_consulta || null })
          })
          .finally(() => {
            this.enviando = false
            this.bajar()
          })
      },
    },
  }
</script>

<style scoped>
  .chat-lista {
    background-color: #fafafa;
  }
  .chat-entrada {
    border-top: 1px solid #e0e0e0;
  }
  .burbuja {
    max-width: 92%;
    padding: 8px 12px;
    border-radius: 10px;
  }
  .burbuja-usuario {
    background-color: #e3f2fd;
  }
  .burbuja-consultor {
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
  }
  .burbuja-error {
    background-color: #ffebee;
    border: 1px solid #ffcdd2;
  }
  .texto-usuario {
    white-space: pre-wrap;
  }
  .chip-sugerencia {
    cursor: pointer;
  }
  .markdown ::v-deep p {
    margin-bottom: 6px;
  }
  .markdown ::v-deep ul,
  .markdown ::v-deep ol {
    padding-left: 18px;
    margin-bottom: 6px;
  }
  .markdown ::v-deep h1,
  .markdown ::v-deep h2,
  .markdown ::v-deep h3,
  .markdown ::v-deep h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 8px 0 4px;
  }
  .markdown ::v-deep table {
    border-collapse: collapse;
    font-size: 0.8rem;
    margin-bottom: 6px;
    display: block;
    overflow-x: auto;
  }
  .markdown ::v-deep th,
  .markdown ::v-deep td {
    border: 1px solid #e0e0e0;
    padding: 2px 6px;
  }
  .markdown ::v-deep hr {
    border: 0;
    border-top: 1px solid #e0e0e0;
    margin: 8px 0;
  }
</style>
